import type { ToolAccessMode } from "@amoreg/contracts";
import { create } from "zustand";

export const DEFAULT_TOOL_ACCESS_MODE = "full-access" satisfies ToolAccessMode;

/** A captured message can never retain more access than the current choice. */
export function resolveToolAccessForSend(
  current: ToolAccessMode,
  captured: ToolAccessMode | undefined,
): ToolAccessMode {
  return current === "full-access" && captured === "full-access"
    ? "full-access"
    : "approval-required";
}

/**
 * Chat-local access choices, keyed by scoped thread key. Shared rather than
 * held by one view, so a choice made in Home's prompt box is the one the chat
 * view sends with once Home hands the draft over.
 */
interface ToolAccessChoicesState {
  readonly choices: Readonly<Record<string, ToolAccessMode>>;
}

export const useToolAccessChoiceStore = create<ToolAccessChoicesState>(() => ({ choices: {} }));

export function readToolAccessChoice(threadKey: string | null): ToolAccessMode {
  if (threadKey === null) return DEFAULT_TOOL_ACCESS_MODE;
  return useToolAccessChoiceStore.getState().choices[threadKey] ?? DEFAULT_TOOL_ACCESS_MODE;
}

export function setToolAccessChoice(threadKey: string, mode: ToolAccessMode): void {
  useToolAccessChoiceStore.setState((state) =>
    state.choices[threadKey] === mode ? state : { choices: { ...state.choices, [threadKey]: mode } },
  );
}

/** The access a chat sends with: its own choice, else the default. */
export function useToolAccessChoice(threadKey: string | null): ToolAccessMode {
  return useToolAccessChoiceStore((state) =>
    threadKey === null
      ? DEFAULT_TOOL_ACCESS_MODE
      : (state.choices[threadKey] ?? DEFAULT_TOOL_ACCESS_MODE),
  );
}
